/**
 * Mode-aware field rules of the connection form (Option C, C0, plan §3.1 "Mode-aware validation").
 *
 * One table, in visual order, says for every validated or focusable field WHEN it applies to the values on screen,
 * how it validates, and where its control is. The form renders a field only while it applies (so every applicable
 * field is mounted), its TanStack validators answer nothing while it does not (`fieldRuleValidators.ts`), stale errors
 * of fields that stopped applying are cleared (`reconcileFieldMeta.ts`), and the Test / Load / Save gate focuses the
 * first blocking field in this order (`blockingIssue.ts`).
 *
 * Why applicability lives here and not in the components: TanStack keeps a field's instance, validators and errors after
 * the field unmounts, and `validateAllFields('submit')` runs them anyway — an SSH user error kept validating after SSH
 * was switched off, an Oracle Server error after TNS was chosen, and Save stayed silently blocked (B8).
 */
import type { ConnectionFormValues, DatabaseEngine } from '../../components/connectionFormSchema';
import { connectionFormSchema, validateKustoTenantId } from '../../components/connectionFormSchema';
import { storageShowsLogin, storageShowsSecret } from '../../components/azureStorageFormRules';
import type { DefaultableField } from '../../components/connectionFieldDefaults';
import { isNetworkEngine } from './engineDrafts';
import { targetLabel } from '../fields/fieldLabels';

type Values = Partial<ConnectionFormValues>;

const KUSTO_URL_HOLDS_PROPERTIES = 'Cluster URL is only the https:// address. Paste a whole connection string with Paste connection string.';
type Auth = ConnectionFormValues['authentication'] | undefined;

export type FieldRuleId =
  | 'server'
  | 'port'
  | 'authentication'
  | 'login'
  | 'password'
  | 'database'
  | 'entraTenantId'
  | 'oracleTnsDescriptor'
  | 'oracleTnsAlias'
  | 'sshTunnel.host'
  | 'sshTunnel.port'
  | 'sshTunnel.user'
  | 'sshTunnel.auth';

export type FieldRule = {
  /** The TanStack field name (and the key of its `fieldMeta`). */
  field: FieldRuleId;
  /** The control that gets focus when this field blocks a Test / Load / Save. */
  selector: string;
  applies: (values: Values) => boolean;
  /** An error message, or undefined when valid. Called only while the field applies. */
  validate?: (value: unknown, values: Values) => string | undefined;
  /**
   * The connection needs a value here to connect (C1): after a prefill, focus goes to the first applicable, visible,
   * required, empty field. Called only while the field applies; absent means optional.
   */
  required?: (values: Values) => boolean;
  /** A per-engine value Tab fills into the empty field ("Press Tab to fill …"). */
  defaultable?: DefaultableField;
};

/** Auth modes that ask for a login and a password (every engine but Azure Storage, which asks per mode). */
export function requiresCredentials(auth: Auth): boolean {
  return auth === 'SQL Server authentication' || auth === 'Password' || auth === 'Service Principal';
}

/** Entra / Azure CLI / Service Principal all take an optional tenant override. */
export function isAzureIdentityAuth(auth: Auth): boolean {
  return auth === 'Microsoft Entra ID (Interactive Browser)' || auth === 'Azure CLI' || auth === 'Service Principal';
}

export function isEntraAuth(auth: Auth): boolean {
  return auth === 'Microsoft Entra ID (Device Code)' || auth === 'Microsoft Entra ID (Interactive Browser)';
}

export function oracleConnectMode(values: Values): NonNullable<ConnectionFormValues['oracleConnectionStringType']> {
  return values.oracleConnectionStringType ?? 'ezconnect';
}

/** Oracle's TNS descriptor and TNS alias modes address the server themselves: no Server, no Port. */
function dialsServerAndPort(values: Values): boolean {
  return values.engine !== 'oracle' || oracleConnectMode(values) === 'ezconnect';
}

function sshApplies(values: Values): boolean {
  return isNetworkEngine(values.engine as DatabaseEngine | undefined) && values.sshTunnel?.enabled === true;
}

function blank(value: unknown): boolean {
  return typeof value !== 'string' || value.trim().length === 0;
}

const always = (): boolean => true;

function zodMessage(schema: { safeParse: (value: unknown) => { success: boolean; error?: { issues: Array<{ message: string }> } } }, value: unknown): string | undefined {
  const parsed = schema.safeParse(value);
  return parsed.success ? undefined : parsed.error?.issues[0]?.message;
}

export function validateSshHost(value: string): string | undefined {
  if (!value) return 'SSH host is required';
  // Defense in depth: the known_hosts grammar's separators (the NUL byte included) — sshKnownHosts.ts has the same guard.
  // eslint-disable-next-line no-control-regex
  if (/[\s,[\]\x00]/.test(value)) return 'SSH host must not contain whitespace, commas, or brackets';
  return undefined;
}

export function validateSshUser(value: string): string | undefined {
  return value ? undefined : 'SSH user is required';
}

export function validateSshPort(value: number): string | undefined {
  if (!Number.isFinite(value) || !Number.isInteger(value)) return 'Port must be an integer';
  if (value < 1 || value > 65535) return 'Port must be in range 1–65535';
  return undefined;
}

export function validateSshKeyPath(value: string): string | undefined {
  return value ? undefined : 'Key file is required';
}

/** Every rule, in the order the fields appear on screen. */
export const FIELD_RULES: readonly FieldRule[] = [
  // Oracle's connect mode comes first (C2): in a TNS mode the alias / descriptor stands where Server and Port are.
  {
    field: 'oracleTnsDescriptor',
    selector: '#oracleTnsDescriptor',
    applies: (values) => values.engine === 'oracle' && oracleConnectMode(values) === 'tns_descriptor',
    validate: (value) => (blank(value) ? 'TNS descriptor is required' : undefined),
    required: always,
  },
  {
    field: 'oracleTnsAlias',
    selector: '#oracleTnsAlias',
    applies: (values) => values.engine === 'oracle' && oracleConnectMode(values) === 'tns_alias',
    validate: (value) => (blank(value) ? 'TNS alias is required' : undefined),
    required: always,
  },
  {
    field: 'server',
    selector: '#server',
    applies: dialsServerAndPort,
    // The message names the field as its label does (C2): "Host is required", "Resource URL is required" …
    validate: (value, values) => {
      if (blank(value)) return `${targetLabel(values)} is required`;
      // C3-11: a Kusto connection string can start with its cluster URL; pasted whole into the field, its properties
      // (an AppKey included) would be shown and saved as the address.
      if (values.engine === 'kusto' && String(value).includes(';')) return KUSTO_URL_HOLDS_PROPERTIES;
      return undefined;
    },
    required: always,
    defaultable: 'server',
  },
  {
    field: 'port',
    selector: '#port',
    applies: (values) => isNetworkEngine(values.engine as DatabaseEngine | undefined) && dialsServerAndPort(values),
    validate: (value) => zodMessage(connectionFormSchema.shape.port, value),
    required: always,
  },
  {
    field: 'authentication',
    selector: '#authentication',
    applies: (values) => values.engine !== 'sqlite',
    validate: (value) => zodMessage(connectionFormSchema.shape.authentication, value),
  },
  {
    field: 'login',
    selector: '#login',
    applies: (values) => (values.engine === 'azurestorage' ? !!values.authentication && storageShowsLogin(values.authentication) : requiresCredentials(values.authentication)),
    required: always,
    defaultable: 'login',
  },
  {
    field: 'password',
    selector: '#password',
    applies: (values) => (values.engine === 'azurestorage' ? !!values.authentication && storageShowsSecret(values.authentication) : requiresCredentials(values.authentication)),
    required: always,
  },
  {
    field: 'entraTenantId',
    selector: '#entraTenantId',
    applies: (values) =>
      (values.engine === 'mssql' && isEntraAuth(values.authentication)) ||
      values.engine === 'kusto' ||
      (values.engine === 'azurestorage' && isAzureIdentityAuth(values.authentication)),
    validate: (value, values) =>
      values.engine === 'kusto' || values.engine === 'azurestorage'
        ? validateKustoTenantId(values.authentication ?? 'Password', value as string | undefined)
        : undefined,
    required: (values) => (values.engine === 'kusto' || values.engine === 'azurestorage') && values.authentication === 'Service Principal',
  },
  // The tenant follows the sign-in fields and comes before Database (C2: mode-first order).
  {
    field: 'database',
    selector: '#database',
    applies: (values) => values.engine !== 'sqlite' && values.engine !== 'azurestorage',
    defaultable: 'database',
  },
  { field: 'sshTunnel.host', selector: '[data-testid="ssh-host-input"]', applies: sshApplies, validate: (value) => validateSshHost(String(value ?? '')), required: always },
  { field: 'sshTunnel.port', selector: '[data-testid="ssh-port-input"]', applies: sshApplies, validate: (value) => validateSshPort(Number(value)), required: always },
  { field: 'sshTunnel.user', selector: '[data-testid="ssh-user-input"]', applies: sshApplies, validate: (value) => validateSshUser(String(value ?? '')), required: always },
  {
    // The key file lives inside the `auth` union; its validator is on the `sshTunnel.auth` field.
    field: 'sshTunnel.auth',
    selector: '[data-testid="ssh-keypath-input"]',
    applies: (values) => sshApplies(values) && values.sshTunnel?.auth?.kind === 'key',
    validate: (value) => validateSshKeyPath(String((value as { keyPath?: string } | undefined)?.keyPath ?? '')),
    required: always,
  },
];

export function fieldRule(field: FieldRuleId): FieldRule {
  const rule = FIELD_RULES.find((candidate) => candidate.field === field);
  if (!rule) throw new Error(`No connection field rule for "${field}"`);
  return rule;
}

export function fieldApplies(field: FieldRuleId, values: Values): boolean {
  return fieldRule(field).applies(values);
}

function readPath(values: Values, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => (acc && typeof acc === 'object' ? (acc as Record<string, unknown>)[key] : undefined), values);
}

/** A value the connection still lacks: blank text (the SSH key file is its `keyPath`); numbers never count as empty. */
function lacksValue(rule: FieldRule, values: Values): boolean {
  const value = readPath(values, rule.field);
  if (rule.field === 'sshTunnel.auth') return blank((value as { keyPath?: string } | undefined)?.keyPath);
  return typeof value === 'number' ? false : blank(value);
}

/** The applicable, required fields that are still empty, in screen order (the prefill focus candidates, C1). */
export function requiredEmptyFields(values: Values): FieldRule[] {
  return FIELD_RULES.filter((rule) => rule.applies(values) && rule.required?.(values) === true && lacksValue(rule, values));
}

/** The field's error for these values; always undefined while the field does not apply. */
export function validateFieldRule(field: FieldRuleId, value: unknown, values: Values): string | undefined {
  const rule = fieldRule(field);
  if (!rule.applies(values)) return undefined;
  return rule.validate?.(value, values);
}
